import { RouteObject } from 'react-router-dom';
import { lazy } from 'react';

// Lazy load components
const HomePage = lazy(() => import('../pages/home/page'));
const AboutPage = lazy(() => import('../pages/about/page'));
const ContactPage = lazy(() => import('../pages/contact/page'));
const PricingPage = lazy(() => import('../pages/pricing/page'));
const NotFoundPage = lazy(() => import('../pages/NotFound'));

// Auth pages
const LoginPage = lazy(() => import('../pages/login/page'));
const RegisterPage = lazy(() => import('../pages/register/page'));
const RegisterStep2Page = lazy(() => import('../pages/register/step-2/page'));
const ResetPasswordPage = lazy(() => import('../pages/reset-password/page'));
const DashboardPage = lazy(() => import('../pages/dashboard/page'));

// Service pages
const BookkeepingPage = lazy(() => import('../pages/bookkeeping/page'));
const TaxPlanningPage = lazy(() => import('../pages/tax-planning/page'));
const FinancialConsultingPage = lazy(() => import('../pages/financial-consulting/page'));
const AuditSupportPage = lazy(() => import('../pages/audit-support/page'));
const CostAccountingPage = lazy(() => import('../pages/cost-accounting/page'));

// Industry pages
const HealthcarePage = lazy(() => import('../pages/healthcare/page'));
const PharmaciesPage = lazy(() => import('../pages/healthcare/pharmacies/page'));
const WellnessBeautyPage = lazy(() => import('../pages/wellness-beauty/page'));
const RestaurantsPage = lazy(() => import('../pages/restaurants/page'));
const RetailPage = lazy(() => import('../pages/retail/page'));
const RealEstatePage = lazy(() => import('../pages/real-estate/page'));
const ConstructionPage = lazy(() => import('../pages/construction/page'));
const ProfessionalServicesPage = lazy(() => import('../pages/professional-services/page'));
const MedicalBillingPage = lazy(() => import('../pages/medical-billing/page'));

// Plan pages
const StarterPlanPage = lazy(() => import('../pages/starter-plan/page'));
const BusinessPlanPage = lazy(() => import('../pages/business-plan/page'));
const ProfessionalPlanPage = lazy(() => import('../pages/professional-plan/page'));
const GrowthPlanPage = lazy(() => import('../pages/growth-plan/page'));
const EnterprisePlanPage = lazy(() => import('../pages/enterprise-plan/page'));

// Other pages
const CheckoutPage = lazy(() => import('../pages/checkout/page'));
const DocumentUploadPage = lazy(() => import('../pages/document-upload/page'));
const DownloadsPage = lazy(() => import('../pages/downloads/page'));
const ResourcesPage = lazy(() => import('../pages/resources/page'));
const MSBCHelpPage = lazy(() => import('../pages/msbc-help/page'));
const SignupPharmacyPage = lazy(() => import('../pages/signup/pharmacy/page'));
const WellnessBeautySignupPage = lazy(() => import('../pages/signup/wellness-beauty-free-trial/page'));
const WellnessProfessionalPlanSignupPage = lazy(() => import('../pages/signup/wellness-professional-plan/page'));
const PrivacyStatementPage = lazy(() => import('../pages/privacy-statement/page'));
const TermsConditionsPage = lazy(() => import('../pages/terms-conditions/page'));

// Spanish pages
import EsPharmaciesPage from '../pages/es/healthcare/pharmacies/page';
import EsHomePage from '../pages/es/home/page';

const CompanyProfilePage = lazy(() => import('../pages/company-profile/page'));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <HomePage />
  },
  {
    path: '/about',
    element: <AboutPage />
  },
  {
    path: '/contact',
    element: <ContactPage />
  },
  {
    path: '/pricing',
    element: <PricingPage />
  },
  {
    path: '/company-profile',
    element: <CompanyProfilePage />,
  },
  
  // Auth routes
  {
    path: '/login',
    element: <LoginPage />
  },
  {
    path: '/register',
    element: <RegisterPage />
  },
  {
    path: '/register/step-2',
    element: <RegisterStep2Page />
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />
  },
  {
    path: '/dashboard',
    element: <DashboardPage />
  },
  
  // Service routes
  {
    path: '/bookkeeping',
    element: <BookkeepingPage />
  },
  {
    path: '/tax-planning',
    element: <TaxPlanningPage />
  },
  {
    path: '/financial-consulting',
    element: <FinancialConsultingPage />
  },
  {
    path: '/audit-support',
    element: <AuditSupportPage />
  },
  {
    path: '/cost-accounting',
    element: <CostAccountingPage />
  },
  
  // Industry routes
  {
    path: '/healthcare',
    element: <HealthcarePage />
  },
  {
    path: '/healthcare/pharmacies',
    element: <PharmaciesPage />
  },
  {
    path: '/wellness-beauty',
    element: <WellnessBeautyPage />
  },
  {
    path: '/restaurants',
    element: <RestaurantsPage />
  },
  {
    path: '/retail',
    element: <RetailPage />
  },
  {
    path: '/real-estate',
    element: <RealEstatePage />
  },
  {
    path: '/construction',
    element: <ConstructionPage />
  },
  {
    path: '/professional-services',
    element: <ProfessionalServicesPage />
  },
  {
    path: '/medical-billing',
    element: <MedicalBillingPage />
  },
  
  // Plan routes
  {
    path: '/starter-plan',
    element: <StarterPlanPage />
  },
  {
    path: '/business-plan',
    element: <BusinessPlanPage />
  },
  {
    path: '/professional-plan',
    element: <ProfessionalPlanPage />
  },
  {
    path: '/growth-plan',
    element: <GrowthPlanPage />
  },
  {
    path: '/enterprise-plan',
    element: <EnterprisePlanPage />
  },
  
  // Signup routes
  {
    path: '/signup/pharmacy',
    element: <SignupPharmacyPage />
  },
  {
    path: '/signup/wellness-beauty-free-trial',
    element: <WellnessBeautySignupPage />
  },
  {
    path: '/signup/wellness-professional-plan',
    element: <WellnessProfessionalPlanSignupPage />
  },
  
  // Other routes
  {
    path: '/checkout',
    element: <CheckoutPage />
  },
  {
    path: '/document-upload',
    element: <DocumentUploadPage />
  },
  {
    path: '/downloads',
    element: <DownloadsPage />
  },
  {
    path: '/resources',
    element: <ResourcesPage />
  },
  {
    path: '/msbc-help',
    element: <MSBCHelpPage />
  },
  {
    path: '/privacy-statement',
    element: <PrivacyStatementPage />
  },
  {
    path: '/terms-conditions',
    element: <TermsConditionsPage />
  },

  // Spanish routes
  {
    path: '/es/',
    element: <EsHomePage />
  },
  {
    path: '/es/healthcare/pharmacies',
    element: <EsPharmaciesPage />
  },
  
  // 404 route
  {
    path: '*',
    element: <NotFoundPage />
  }
];

export default routes;
